import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider
} from '@mui/material';

interface RefundRequestDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  request: any;
}

const RefundRequestDetailsDialog: React.FC<RefundRequestDetailsDialogProps> = ({
  open,
  onClose,
  request
}) => {
  if (!request) return null;

  const formatDate = (date: any) => {
    return date ? new Date(date).toLocaleString() : 'N/A';
  };

  const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' => {
    const colorMap: { [key: string]: 'warning' | 'info' | 'success' | 'error' } = {
      pending: 'warning',
      approved: 'info',
      processed: 'success',
      rejected: 'error',
      failed: 'error'
    };
    return colorMap[status] || 'warning';
  };

  const getItemTypeName = (type: string) => {
    const typeMap: { [key: string]: string } = {
      adSpace: 'Ad Space',
      tokenPlan: 'Token Plan',
      subscriptionPlan: 'Subscription Plan',
      purchasePlan: 'Purchase Plan'
    };
    return typeMap[type] || type;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Refund Request Details - ID #{request.id}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Status */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Chip label={request.status} color={getStatusColor(request.status)} />
          </Box>

          <Divider />

          {/* User Information */}
          <Box>
            <Typography variant="h6" gutterBottom>User Information</Typography>
            <Typography variant="body2"><strong>User ID:</strong> {request.userId}</Typography>
            <Typography variant="body2"><strong>Name:</strong> {request.user?.name || 'N/A'}</Typography>
            <Typography variant="body2"><strong>Email:</strong> {request.user?.email || 'N/A'}</Typography>
          </Box>

          <Divider />

          {/* Payment Information */}
          <Box>
            <Typography variant="h6" gutterBottom>Payment Information</Typography>
            <Typography variant="body2"><strong>Payment ID:</strong> {request.paymentId}</Typography>
            <Typography variant="body2"><strong>Payment Intent ID:</strong> {request.paymentIntentId}</Typography>
            <Typography variant="body2">
              <strong>Payment Date:</strong> {formatDate(request.payment?.completedAt)}
            </Typography>
          </Box>

          <Divider />

          {/* Item Information */}
          <Box>
            <Typography variant="h6" gutterBottom>Item Information</Typography>
            <Typography variant="body2"><strong>Type:</strong> {getItemTypeName(request.itemType)}</Typography>
            <Typography variant="body2"><strong>Item ID:</strong> {request.itemId}</Typography>
            <Typography variant="body2">
              <strong>Item Name:</strong> {request.itemDetails?.itemName || 'N/A'}
            </Typography>
            <Typography variant="body2">
              <strong>Refund Amount:</strong> ${parseFloat(request.refundAmount).toFixed(2)} {request.itemDetails?.currencyCode || 'USD'}
            </Typography>
          </Box>

          <Divider />

          {/* Reason for Refund */}
          <Box>
            <Typography variant="h6" gutterBottom>Reason for Refund</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {request.reason || 'No reason provided'}
            </Typography>
          </Box>

          <Divider />

          {/* Request Timeline */}
          <Box>
            <Typography variant="h6" gutterBottom>Timeline</Typography>
            <Typography variant="body2"><strong>Requested:</strong> {formatDate(request.created_at)}</Typography>
            {request.reviewedAt && (
              <Typography variant="body2"><strong>Reviewed:</strong> {formatDate(request.reviewedAt)}</Typography>
            )}
            {request.updated_at && (
              <Typography variant="body2"><strong>Last Updated:</strong> {formatDate(request.updated_at)}</Typography>
            )}
          </Box>

          {/* Review Information (if reviewed) */}
          {request.reviewedBy && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" gutterBottom>Review Information</Typography>
                <Typography variant="body2">
                  <strong>Reviewed By:</strong> {request.reviewer?.name || request.reviewer?.email || `ID: ${request.reviewedBy}`}
                </Typography>
                {request.reviewNotes && (
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                    <strong>Review Notes:</strong><br />
                    {request.reviewNotes}
                  </Typography>
                )}
                {request.processedRefundId && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Stripe Refund ID:</strong> {request.processedRefundId}
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Item Details (JSON) */}
          <Divider />
          <Box>
            <Typography variant="h6" gutterBottom>Item Details (Full)</Typography>
            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto'
              }}
            >
              <pre style={{ margin: 0, fontSize: '12px' }}>
                {JSON.stringify(request.itemDetails, null, 2)}
              </pre>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundRequestDetailsDialog;
